import { type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import type { DefaultJobConversationViewModel } from '@/features/jobs/details/JobConversationViewModel'
import { ConversationMessageListContainer } from '@/features/messages/components/ConversationMessageList/ConversationMessageListContainer'
import { ConversationLayoutContainer } from '@/features/messages/components/ConversationLayout/ConversationLayoutContainer'
import { ConversationMessageComposerContainer } from '@/features/messages/components/ConversationMessageComposer/ConversationMessageComposerContainer'
import type { ConversationLayoutProps } from '@/features/messages/components/ConversationLayout'

interface DefaultJobConversationContainerProps {
  vm: DefaultJobConversationViewModel

  /** @see ConversationLayoutProps.scrollingElement */
  scrollingElement?: HTMLElement | null

  /** @see ConversationLayoutProps.composerPositioning */
  composerPositioning?: ConversationLayoutProps['composerPositioning']

  /** @see ConversationLayoutProps.isComposerHidden */
  isComposerHidden?: ConversationLayoutProps['isComposerHidden']

  /** @see ConversationLayoutProps.composerMask */
  composerMask?: ConversationLayoutProps['composerMask']

  /** @see ConversationLayoutProps.spacing */
  spacing?: ConversationLayoutProps['spacing']

  /** @see ConversationLayoutProps.isReadyToScroll$ */
  isReadyToScroll$?: ConversationLayoutProps['isReadyToScroll$']

  /** @see ConversationLayoutProps.scrollToBottom$ */
  scrollToBottom$?: ConversationLayoutProps['scrollToBottom$']

  /** @see ConversationLayoutProps.composerLeadingActions */
  composerLeadingActions?: ReactNode

  composerCallout?: ReactNode
}

/**
 * Default conversation container for jobs that don't require special message handling.
 * Used by consultation, bookkeeping, and other job types that use the standard conversation.
 */
export const DefaultJobConversationContainer = observer(
  function DefaultJobConversationContainer({
    vm,
    composerLeadingActions,
    composerCallout,
    ...rest
  }: DefaultJobConversationContainerProps) {
    const isComposerVisible =
      vm.job.status !== 'Closed' && !vm.conversationVm.fetchMessages.pending

    return (
      <ConversationLayoutContainer
        {...rest}
        typing={[]}
        conversationVm={vm.conversationVm}
        chatVm={vm.chatVm}
        composer={
          isComposerVisible && (
            <>
              {composerCallout}
              <ConversationMessageComposerContainer
                vm={vm.conversationVm}
                testId="job-messages-composer"
                textareaTestId="job-messages-composer-textarea"
                sendButtonTestId="job-messages-composer-send-button"
                leadingActions={composerLeadingActions}
              />
            </>
          )
        }
        messageList={
          <ConversationMessageListContainer
            aria-label="Conversation"
            isPending={vm.conversationVm.fetchMessages.pending}
            chatListVm={vm.chatVm}
            conversationVm={vm.conversationVm}
            testId="job-messages-list"
          />
        }
      />
    )
  },
)
